/**
 * SangradorDashboard - Dashboard consolidado de UM sangrador num mês específico.
 *
 * Modal aberto pelo botão "👥 Equipe" no Calendário. Cada card de sangrador na lista
 * abre este modal com os dados do mês selecionado.
 *
 * Consome o endpoint /api/sangradores/{nome}/dashboard que agrega:
 *  - cards: produção, kg/planta/corte, plantas alocadas, dias trabalhados
 *  - ranking dentro do mês (#X de Y, % vs média)
 *  - frequência (sangrias, faltas, compensações, reposições)
 *  - entregas (datas + kg)
 *  - selo (vem do User vinculado, ou null se não houver)
 *  - has_allocation (boolean — se false, mostra empty state direcionando para Configurar Safra)
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { MetaCalculationInfo } from "./MetaCalculationInfo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SangradorDashboard = ({ open, onClose, sangradorNome, propertyId, month, propertyName }) => {
  const [data, setData] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [contextPerf, setContextPerf] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !sangradorNome || !propertyId || !month) return;
    setLoading(true);
    setError(null);
    setData(null);
    setEvolution(null);
    setContextPerf(null);
    const dashboardPromise = axios
      .get(`${API}/sangradores/${encodeURIComponent(sangradorNome)}/dashboard`, {
        params: { property_id: propertyId, month },
      })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Erro ao carregar dashboard do sangrador"));

    // Gráfico da safra inteira — falha silenciosa (não bloqueia dashboard principal)
    const evolutionPromise = axios
      .get(`${API}/sangradores/${encodeURIComponent(sangradorNome)}/evolution`, {
        params: { property_id: propertyId, month },
      })
      .then((r) => setEvolution(r.data))
      .catch(() => setEvolution(null));

    // Meta do clone (Desempenho Contextual) — falha silenciosa (propriedade pode não ter contexto)
    const [yStr, mStr] = month.split("-");
    const contextPromise = axios
      .get(`${API}/contextual-performance/calculate/${propertyId}`, {
        params: { month: parseInt(mStr), year: parseInt(yStr) },
      })
      .then((r) => setContextPerf(r.data?.result || r.data))
      .catch(() => setContextPerf(null));

    Promise.all([dashboardPromise, evolutionPromise, contextPromise]).finally(() => setLoading(false));
  }, [open, sangradorNome, propertyId, month]);

  const renderEmpty = () => (
    <div className="text-center py-8" data-testid="dashboard-empty-state">
      <p className="text-amber-700 bg-amber-50 p-4 rounded-lg border border-amber-200">
        🗓️ <strong>Sem alocação configurada</strong>
        <br />
        <span className="text-sm">
          Configure as plantas por sangrador em <strong>Configurar Safra</strong> para ver o dashboard completo.
        </span>
      </p>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sangrador-dashboard-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-2xl">
            <span>👤</span>
            <span data-testid="dashboard-nome">{sangradorNome}</span>
            {data?.selo && (
              <span
                className="text-base px-3 py-1 rounded-full font-semibold"
                style={{ backgroundColor: `${data.selo.color}22`, color: data.selo.color, border: `1px solid ${data.selo.color}` }}
                title={data.selo.label}
                data-testid="dashboard-selo"
              >
                {data.selo.icon} {data.selo.tier.toUpperCase()}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {propertyName} • {month}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-center py-10" data-testid="dashboard-loading">
            <span className="text-gray-500">Carregando dashboard...</span>
          </div>
        )}

        {error && (
          <div className="text-center py-6" data-testid="dashboard-error">
            <p className="text-red-700 bg-red-50 p-3 rounded-lg border border-red-200">⚠️ {error}</p>
          </div>
        )}

        {data && !loading && (
          <div className="space-y-5 mt-2">
            {/* Empty state se não há alocação */}
            {!data.has_allocation && renderEmpty()}

            {/* Cards principais */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="dashboard-cards">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-emerald-700">
                  {(data.cards.producao_kg || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}<span className="text-sm ml-1">kg</span>
                </div>
                <div className="text-xs text-gray-600 mt-1">Produção no mês</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-blue-700">
                  {data.cards.kg_planta_corte != null ? data.cards.kg_planta_corte.toFixed(4) : "—"}
                </div>
                <div className="text-xs text-gray-600 mt-1">kg/planta/corte</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                <div className="text-2xl font-bold text-amber-700">
                  {data.cards.plantas_alocadas != null ? data.cards.plantas_alocadas.toLocaleString("pt-BR") : "—"}
                </div>
                <div className="text-xs text-gray-600 mt-1">Plantas alocadas</div>
              </div>
              <div
                className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-center"
                data-testid="dashboard-card-entregas"
              >
                <div className="text-2xl font-bold text-indigo-700">
                  {data.cards.entregas_count ?? 0}
                </div>
                <div className="text-xs text-gray-600 mt-1">Entregas</div>
              </div>
              <div
                className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center"
                data-testid="dashboard-card-cortes"
                title={
                  data.cards.cortes_prorated
                    ? "Prorrateado pela vigência do sangrador no mês (entrou ou saiu no meio do mês)."
                    : "Cortes realizados no mês (sangrias + reposições)."
                }
              >
                <div className="text-2xl font-bold text-purple-700 flex items-center justify-center gap-1">
                  {(data.cards.cortes_no_mes ?? 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}
                  {data.cards.cortes_prorated && (
                    <span className="text-[10px] text-purple-500" aria-label="prorrateado">🛈</span>
                  )}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  Cortes no mês{data.cards.cortes_prorated ? "*" : ""}
                </div>
              </div>
            </div>
            {data.cards.cortes_prorated && (
              <p className="text-xs text-purple-700 -mt-2" data-testid="dashboard-prorated-note">
                * Cortes prorrateados pela vigência deste sangrador no mês.
              </p>
            )}

            {/* Ranking */}
            {data.ranking?.posicao != null && (
              <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid="dashboard-ranking">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm text-gray-600">Posição no ranking do mês</span>
                    <div className="text-lg font-bold mt-1">
                      #{data.ranking.posicao} de {data.ranking.total}
                      {data.ranking.posicao === 1 && <span className="ml-2 text-yellow-600">🏆 líder</span>}
                    </div>
                  </div>
                  {data.ranking.percent_vs_avg != null && (
                    <div className="text-right">
                      <span className="text-xs text-gray-500">vs média da equipe</span>
                      <div
                        className={`text-lg font-bold ${
                          data.ranking.percent_vs_avg > 0 ? "text-emerald-600" : data.ranking.percent_vs_avg < 0 ? "text-red-600" : "text-gray-500"
                        }`}
                      >
                        {data.ranking.percent_vs_avg > 0 ? "+" : ""}
                        {data.ranking.percent_vs_avg.toFixed(1)}%
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Você vs Meta do Clone (Desempenho Contextual + DRC) */}
            {(() => {
              const goalG = contextPerf?.goal?.target_grams;
              const drc = contextPerf?.production?.drc_percentage || 53;
              const kgPlantaCorte = data.cards.kg_planta_corte;
              if (!goalG || kgPlantaCorte == null) return null;
              const wetG = kgPlantaCorte * 1000;
              const dryG = wetG * (drc / 100);
              const pct = (dryG / goalG) * 100;
              const cls = pct >= 110 ? { emoji: "🌟", label: "EXCELENTE", color: "#ca8a04", bg: "#fefce8" }
                : pct >= 100 ? { emoji: "✅", label: "ATINGIU META", color: "#16a34a", bg: "#f0fdf4" }
                : pct >= 85 ? { emoji: "👍", label: "BOM", color: "#2563eb", bg: "#eff6ff" }
                : pct >= 70 ? { emoji: "📈", label: "REGULAR", color: "#d97706", bg: "#fffbeb" }
                : { emoji: "💪", label: "ABAIXO DA META", color: "#ea580c", bg: "#fff7ed" };
              const clone = contextPerf?.context?.clone || "clone";
              return (
                <div className="border rounded-lg p-3" style={{ backgroundColor: cls.bg, borderColor: `${cls.color}55` }} data-testid="dashboard-meta-clone">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-sm font-semibold text-gray-700 inline-flex items-center gap-1">🎯 Você vs Meta do Clone ({clone})
                        <MetaCalculationInfo
                          goal={contextPerf?.goal}
                          context={contextPerf?.context}
                          drc={drc}
                          wetGrams={wetG}
                          dryGrams={dryG}
                        />
                      </span>
                      <div className="text-xs text-gray-600 mt-1">
                        {dryG.toFixed(1)}g seca/planta/corte <span className="text-gray-400">({wetG.toFixed(1)}g úmido × DRC {drc}%)</span> • meta do mês: {goalG.toFixed(1)}g
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold" style={{ color: cls.color }} data-testid="meta-clone-pct">
                        {cls.emoji} {pct.toFixed(0)}%
                      </div>
                      <div className="text-xs font-medium" style={{ color: cls.color }}>{cls.label}</div>
                    </div>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden mt-2">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(pct, 120)}%`, backgroundColor: cls.color }} />
                  </div>
                </div>
              );
            })()}

            {/* Frequência */}
            <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid="dashboard-frequencia">
              <h4 className="font-semibold text-gray-700 mb-2 text-sm flex items-center gap-2">
                <span>📅 Frequência do mês</span>
                {data.frequencia.is_team_metric && (
                  <span
                    className="text-[10px] font-normal text-blue-600 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5"
                    title="Modo equipe: sangrias e reposições são da equipe toda, filtradas pela vigência deste sangrador no mês."
                    data-testid="dashboard-frequencia-team-badge"
                  >
                    equipe
                  </span>
                )}
              </h4>
              {data.frequencia.inactive_this_month ? (
                <p className="text-sm text-gray-500 italic" data-testid="dashboard-frequencia-inactive">
                  Sangrador não estava ativo neste mês (fora da vigência).
                </p>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div>
                      <span className="text-gray-600">Sangria: </span>
                      <strong>{data.frequencia.dias_sangria}</strong> dia(s){data.frequencia.prorated ? "*" : ""}
                    </div>
                    <div>
                      <span className="text-gray-600">Faltas: </span>
                      <strong className={data.frequencia.faltas > 0 ? "text-red-600" : ""}>{data.frequencia.faltas}</strong>
                    </div>
                    <div>
                      <span className="text-gray-600">Compensações: </span>
                      <strong>{data.frequencia.compensacoes}</strong>
                    </div>
                    <div>
                      <span className="text-gray-600">Reposições: </span>
                      <strong>{data.frequencia.reposicoes}</strong>{data.frequencia.prorated ? "*" : ""}
                    </div>
                  </div>
                  {data.frequencia.prorated && (
                    <p className="text-[11px] text-purple-700 mt-2" data-testid="dashboard-frequencia-prorated-note">
                      * Valores prorrateados pela vigência do sangrador no mês.
                    </p>
                  )}
                  {data.frequencia.sangrias_perdidas > 0 && (
                    <p className="text-xs text-amber-700 mt-2">⚠️ {data.frequencia.sangrias_perdidas} sangria(s) perdida(s) neste mês</p>
                  )}
                </>
              )}
            </div>

            {/* Entregas */}
            {(data.entregas || []).length > 0 && (
              <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid="dashboard-entregas">
                <h4 className="font-semibold text-gray-700 mb-2 text-sm">
                  📦 Entregas realizadas ({data.entregas.length})
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-1">Data</th>
                        <th className="text-right py-1">Quantidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.entregas.map((e, i) => (
                        <tr key={i} className="border-b last:border-0">
                          <td className="py-1">{e.data}</td>
                          <td className="text-right font-medium">{e.kg.toLocaleString("pt-BR")} kg</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Estado: sem registros no mês */}
            {!data.is_active_no_mes && data.has_allocation && (
              <div className="text-center py-4" data-testid="dashboard-no-activity">
                <p className="text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200 text-sm">
                  ℹ️ Nenhum registro deste sangrador no mês selecionado. As plantas dele estão temporariamente paralisadas.
                </p>
              </div>
            )}

            {/* Gráfico evolutivo da safra inteira */}
            {evolution && (evolution.months || []).some((m) => m.producao_kg > 0) && (
              <div className="bg-white border border-gray-200 rounded-lg p-3" data-testid="dashboard-evolution">
                <h4 className="font-semibold text-gray-700 mb-2 text-sm">
                  📈 Evolução na safra {evolution.safra_label}
                  {evolution.plantas_alocadas != null && (
                    <span className="text-xs font-normal text-gray-500 ml-2">
                      ({evolution.plantas_alocadas.toLocaleString("pt-BR")} plantas alocadas)
                    </span>
                  )}
                </h4>
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <LineChart
                      data={evolution.months}
                      margin={{ top: 8, right: 12, left: 0, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                      <YAxis
                        yAxisId="kg"
                        orientation="left"
                        tick={{ fontSize: 11 }}
                        label={{ value: "kg", position: "insideLeft", angle: -90, style: { fontSize: 10, fill: "#059669" } }}
                      />
                      <YAxis
                        yAxisId="kgpc"
                        orientation="right"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => (v != null ? v.toFixed(3) : "")}
                        label={{ value: "kg/pl/corte", position: "insideRight", angle: 90, style: { fontSize: 10, fill: "#2563eb" } }}
                      />
                      <Tooltip
                        formatter={(value, name) => {
                          if (name === "producao_kg") return [`${(value || 0).toLocaleString("pt-BR", { maximumFractionDigits: 1 })} kg`, "Produção"];
                          if (name === "kg_planta_corte") return [value != null ? value.toFixed(4) : "—", "kg/planta/corte"];
                          return value;
                        }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 11 }}
                        formatter={(v) => (v === "producao_kg" ? "Produção (kg)" : "kg/planta/corte")}
                      />
                      <Line
                        yAxisId="kg"
                        type="monotone"
                        dataKey="producao_kg"
                        stroke="#059669"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                      <Line
                        yAxisId="kgpc"
                        type="monotone"
                        dataKey="kg_planta_corte"
                        stroke="#2563eb"
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        connectNulls
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {!evolution.has_allocation && (
                  <p className="text-xs text-amber-700 mt-1">
                    ⚠️ Configure as plantas por sangrador em &quot;Configurar Safra&quot; para ver kg/planta/corte.
                  </p>
                )}
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button onClick={onClose} variant="outline" data-testid="dashboard-close-btn">
                Fechar
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SangradorDashboard;
